import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';

import {
  AgentArchitecture,
  AssistantKey,
  ChatMessage,
  ChatMessageRole,
  CitationType,
  ExpectedCitation,
  ExpectedToolCall,
  InvocationContext,
  InvocationContextApp,
  InvocationContextAuthState,
  InvocationContextChannel,
  InvocationContextMedium,
  TestCase,
  TestType,
} from '@vendasta/protos/ai_assistants/v1alpha1';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyUploaderModule } from '@vendasta/galaxy/uploader';
import { MatButtonModule } from '@angular/material/button';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatChipsModule } from '@angular/material/chips';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { getAppName, getAuthStateName, getChannelName, isVoiceTest } from '../test-run.utils';
import { AiAssistantI18nModule } from '../../../assets/i18n/ai-assistant-i18n.module';
import { AiConstraintsForm } from '../../../core/forms/ai-constraints.form';
import { AiConstraintsFieldComponent } from '../../constraints/ai-constraints-field/ai-constraints-field.component';
import { Action } from '@vendasta/protos/ai_assistants/v1alpha1';
import { TranslateService } from '@ngx-translate/core';

function defaultExpectedCitation(): ExpectedCitation {
  return { title: '', type: 'UNSPECIFIED', link: '' } as ExpectedCitation;
}

export class ChatMessageForm {
  role: FormControl<ChatMessageRole>;
  content: FormControl<string>;

  constructor(chatMessage: ChatMessage) {
    this.role = new FormControl(chatMessage.role, [Validators.required]);
    this.content = new FormControl(chatMessage.content, [Validators.required]);
  }

  get valid(): boolean {
    return this.role.valid && this.content.valid;
  }
}

export class ChatHistoryFormArray {
  messages: ChatMessageForm[];
  // Adding/removing elements does not dirty array, use this helper to track changes
  private mutated = false;

  constructor(chatHistory: ChatMessage[]) {
    this.messages = chatHistory.map((message: ChatMessage) => {
      return new ChatMessageForm(message);
    });
  }

  addMessage(): void {
    this.messages.push(
      new ChatMessageForm({
        role: ChatMessageRole.CHAT_MESSAGE_ROLE_USER,
        content: '',
        contentParts: [],
        externalId: '',
      } as ChatMessage),
    );
    this.mutated = true;
  }

  removeMessage(index: number): void {
    this.messages.splice(index, 1);
    this.mutated = true;
  }

  get valid(): boolean {
    return this.messages.every((message: ChatMessageForm) => message.valid);
  }

  get edited(): boolean {
    return this.mutated || this.messages.some((message) => message.role.dirty || message.content.dirty);
  }
}

class ExpectedToolCallForm {
  name: FormControl<string>;
  arguments: FormControl<string>;
  response: FormControl<string>;

  constructor(expectedToolCall: ExpectedToolCall, argsRequired: boolean, responseRequired: boolean) {
    this.name = new FormControl(expectedToolCall.name, [Validators.required]);
    this.arguments = new FormControl(expectedToolCall.arguments, [Validators.required]);
    this.response = new FormControl(expectedToolCall.response, [Validators.required]);

    if (!argsRequired) {
      this.arguments.disable();
    }
    if (!responseRequired) {
      this.response.disable();
    }
    this.name.markAsTouched();
    this.arguments.markAsTouched();
    this.response.markAsTouched();
  }

  valid(): boolean {
    return (
      this.name.valid &&
      (this.arguments.disabled || this.arguments.valid) &&
      (this.response.disabled || this.response.valid)
    );
  }
}

class ExpectedCitationForm {
  title: FormControl<string>;
  type: FormControl<CitationType>;
  link: FormControl<string>;

  constructor(citation: ExpectedCitation) {
    this.title = new FormControl(citation.title ?? '', { nonNullable: true });
    this.type = new FormControl<CitationType>(citation.type ?? 'UNSPECIFIED', { nonNullable: true });
    this.link = new FormControl(citation.link ?? '', { nonNullable: true });
  }

  get valid(): boolean {
    return true;
  }
}

export class ExpectedCitationsFormArray {
  citations: ExpectedCitationForm[];

  constructor(expectedCitations: ExpectedCitation[]) {
    this.citations = expectedCitations.map((c) => new ExpectedCitationForm(c));
  }

  addCitation(): void {
    this.citations.push(new ExpectedCitationForm(defaultExpectedCitation()));
  }

  removeCitation(index: number): void {
    this.citations.splice(index, 1);
  }

  get valid(): boolean {
    return this.citations.every((c) => c.valid);
  }
}

export class ExpectedToolCallsFormArray {
  tools: ExpectedToolCallForm[];
  _requireSpecificArguments = false;
  _requireSpecificResponse = false;

  constructor(expectedToolCalls: ExpectedToolCall[]) {
    this._requireSpecificResponse = expectedToolCalls.some(
      (toolCall: ExpectedToolCall) => toolCall.response && toolCall.response.length > 0,
    );
    this._requireSpecificArguments = expectedToolCalls.some(
      (toolCall: ExpectedToolCall) => toolCall.arguments && toolCall.arguments.length > 0,
    );

    this.tools = expectedToolCalls.map((toolCall: ExpectedToolCall) => {
      return new ExpectedToolCallForm(toolCall, this.requireSpecificArguments, this.requireSpecificResponse);
    });
  }

  addToolCall(): void {
    this.tools.push(
      new ExpectedToolCallForm(
        { name: '', arguments: '', response: '' },
        this.requireSpecificArguments,
        this.requireSpecificResponse,
      ),
    );
  }

  removeToolCall(index: number): void {
    this.tools.splice(index, 1);
  }

  get requireSpecificArguments(): boolean {
    return this._requireSpecificArguments;
  }

  set requireSpecificArguments(require: boolean) {
    this._requireSpecificArguments = require;
    this.tools.forEach((tool: ExpectedToolCallForm) => {
      if (require) {
        tool.arguments.enable();
      } else {
        tool.arguments.disable();
      }
      tool.arguments.markAsTouched();
    });
  }

  get requireSpecificResponse(): boolean {
    return this._requireSpecificResponse;
  }

  set requireSpecificResponse(require: boolean) {
    this._requireSpecificResponse = require;
    this.tools.forEach((tool: ExpectedToolCallForm) => {
      if (require) {
        tool.response.enable();
      } else {
        tool.response.disable();
      }
      tool.response.markAsTouched();
    });
  }

  get valid(): boolean {
    return this.tools.every((tool: ExpectedToolCallForm) => tool.valid());
  }
}

export class TestCaseForm {
  id: FormControl<string>;
  name: FormControl<string>;
  expectation: FormControl<string>;
  testType: FormControl<TestType>;
  callerInstructions: FormControl<string>;
  chatHistory: ChatHistoryFormArray;
  expectedToolCalls: ExpectedToolCallsFormArray;
  expectedCitations: ExpectedCitationsFormArray;
  agentArchitecture: FormControl<AgentArchitecture>;
  isSuccessEvaluator: FormControl<boolean>;
  scenario: FormControl<string>;
  channel: FormControl<InvocationContextChannel>;
  authState: FormControl<InvocationContextAuthState>;
  app: FormControl<InvocationContextApp>;
  constraints: AiConstraintsForm;
  readonly scenarioPreexisting: boolean;
  private readonly assistantKey: AssistantKey | undefined;

  constructor(testCase: TestCase, defaultTestType?: TestType) {
    this.assistantKey = testCase.assistantKey;
    this.scenarioPreexisting = !!testCase.scenario?.trim();
    this.id = new FormControl(testCase.id);
    this.name = new FormControl(testCase.name, [Validators.required]);
    this.expectation = new FormControl(testCase.expectation, [Validators.required]);
    this.callerInstructions = new FormControl(testCase.callerInstructions || '');
    this.chatHistory = new ChatHistoryFormArray(testCase.chatHistory || []);
    this.expectedToolCalls = new ExpectedToolCallsFormArray(testCase.expectedToolCalls || []);
    this.expectedCitations = new ExpectedCitationsFormArray(testCase.expectedCitations ?? []);
    this.isSuccessEvaluator = new FormControl(testCase.isSuccessEvaluator ?? false, { nonNullable: true });
    this.scenario = new FormControl(testCase.scenario ?? '', { nonNullable: true });
    this.onEvaluatorToggle(this.isSuccessEvaluator.value);

    const testTypeValue: TestType =
      testCase.testType && testCase.testType !== 'TEST_TYPE_UNSPECIFIED'
        ? testCase.testType
        : (defaultTestType ?? TestType.TEST_TYPE_CHAT);
    this.testType = new FormControl(testTypeValue);

    const agentArch: AgentArchitecture =
      testCase.agentArchitecture && testCase.agentArchitecture !== 'UNSET'
        ? testCase.agentArchitecture
        : AgentArchitecture.STANDARD;
    this.agentArchitecture = new FormControl(agentArch);

    const context = testCase.invocationContext;
    const storedChannel =
      context?.channel && context.channel !== InvocationContextChannel.UNSET_CHANNEL ? context.channel : undefined;
    const defaultChannel = isVoiceTest(testTypeValue)
      ? InvocationContextChannel.PHONE
      : InvocationContextChannel.WEBCHAT;
    // A case without a stored channel falls back to the test-type default.
    this.channel = new FormControl(storedChannel ?? defaultChannel, { nonNullable: true });

    // Auth state and app are only editable for the PLATFORM channel; elsewhere the
    // controls are hidden and toTestCase emits UNSET. They default to unset so a
    // PLATFORM case can leave them unspecified.
    this.authState = new FormControl(context?.authState ?? InvocationContextAuthState.UNSET_AUTH_STATE, {
      nonNullable: true,
    });
    this.app = new FormControl(context?.app ?? InvocationContextApp.UNSET_APP, { nonNullable: true });

    this.constraints = new AiConstraintsForm(testCase.constraints ?? []);
  }

  get isVoice(): boolean {
    return isVoiceTest(this.testType.value);
  }

  get scenarioActive(): boolean {
    return this.isSuccessEvaluator.value && !!this.scenario.value?.trim();
  }

  get inputEdited(): boolean {
    return this.isVoice ? this.callerInstructions.dirty : this.chatHistory.edited;
  }

  onEvaluatorToggle(on: boolean): void {
    if (on) {
      this.scenario.setValidators((c) => (c.value?.trim() ? null : { required: true }));
    } else {
      this.scenario.clearValidators();
    }
    this.scenario.updateValueAndValidity();
  }

  get valid(): boolean {
    const base = this.name.valid && this.expectation.valid && this.expectedToolCalls.valid && this.scenario.valid;
    if (this.isVoice) {
      return base && this.callerInstructions.value?.length > 0;
    }
    return base && this.chatHistory.valid;
  }

  toTestCase(): Partial<TestCase> {
    const expectedToolCalls: ExpectedToolCall[] = this.expectedToolCalls.tools.map(
      (toolCall: ExpectedToolCallForm) =>
        ({
          name: toolCall.name.value,
          arguments: toolCall.arguments.value || '',
          response: toolCall.response.value || '',
        }) as ExpectedToolCall,
    );

    const expectedCitations: ExpectedCitation[] = this.expectedCitations.citations
      .map(
        (c: ExpectedCitationForm) =>
          ({
            title: c.title.value || '',
            type: c.type.value,
            link: c.link.value || '',
          }) as ExpectedCitation,
      )
      .filter((c) => c.title || c.link || (c.type && c.type !== 'UNSPECIFIED'));

    const isPlatform = this.channel.value === InvocationContextChannel.PLATFORM;
    const invocationContext: InvocationContext = {
      medium: this.isVoice ? InvocationContextMedium.VOICE : InvocationContextMedium.TEXT,
      channel: this.channel.value,
      authState: isPlatform ? this.authState.value : InvocationContextAuthState.UNSET_AUTH_STATE,
      app: isPlatform ? this.app.value : InvocationContextApp.UNSET_APP,
    };

    const base = {
      id: this.id.value,
      name: this.name.value,
      expectation: this.expectation.value,
      testType: this.testType.value,
      expectedToolCalls,
      expectedCitations,
      assistantKey: this.assistantKey,
      isSuccessEvaluator: this.isSuccessEvaluator.value,
      scenario: this.isSuccessEvaluator.value ? this.scenario.value.trim() : '',
      invocationContext,
      constraints: this.constraints.toConstraints() as TestCase['constraints'],
    };

    if (this.isVoice) {
      return {
        ...base,
        callerInstructions: this.callerInstructions.value,
        chatHistory: [],
        agentArchitecture: AgentArchitecture.UNSET,
      };
    }

    const chatHistory: ChatMessage[] = this.chatHistory.messages.map(
      (message: ChatMessageForm) =>
        ({
          role: message.role.value,
          content: message.content.value,
          contentParts: [],
          externalId: '',
        }) as ChatMessage,
    );

    return {
      ...base,
      callerInstructions: '',
      chatHistory,
      agentArchitecture: this.agentArchitecture.value,
    };
  }
}

@Component({
  selector: 'ai-test-case-edit-modal',
  imports: [
    GalaxyAlertModule,
    GalaxyFormFieldModule,
    GalaxyUploaderModule,
    MatButtonModule,
    MatIconModule,
    MatDialogActions,
    MatDialogContent,
    MatDialogTitle,
    ReactiveFormsModule,
    MatRadioModule,
    MatSelectModule,
    MatInputModule,
    MatSlideToggleModule,
    MatTooltipModule,
    MatChipsModule,
    ClipboardModule,
    FormsModule,
    AiAssistantI18nModule,
    AiConstraintsFieldComponent,
  ],

  templateUrl: './test-case-edit-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './test-case-edit-modal.component.scss',
})
export class TestCaseEditModalComponent implements OnInit {
  protected data = inject(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<TestCaseEditModalComponent>);
  private readonly translateService = inject(TranslateService);
  form: TestCaseForm;
  protected readonly invokeOnlyActions = [Action.ACTION_INVOKE];
  chatRoles = ChatMessageRole;
  agentArchitectures = AgentArchitecture;
  citationTypes = CitationType;
  authStates = InvocationContextAuthState;
  apps = InvocationContextApp;
  chatChannelOptions: InvocationContextChannel[] = [
    InvocationContextChannel.WEBCHAT,
    InvocationContextChannel.SMS,
    InvocationContextChannel.WHATSAPP,
    InvocationContextChannel.FACEBOOK,
    InvocationContextChannel.INSTAGRAM,
    InvocationContextChannel.EMAIL,
    InvocationContextChannel.PLATFORM,
    InvocationContextChannel.SNAPSHOT,
  ];
  voiceChannelOptions: InvocationContextChannel[] = [
    InvocationContextChannel.PHONE,
    InvocationContextChannel.PLATFORM,
  ];
  protected idCopied = false;

  get channelOptions(): InvocationContextChannel[] {
    return this.form.isVoice ? this.voiceChannelOptions : this.chatChannelOptions;
  }

  // Auth state and app only apply when the assistant is invoked from within the
  // platform (Business/Partner Center); other channels carry no app or login state.
  get showAuthAndApp(): boolean {
    return this.form.channel.value === InvocationContextChannel.PLATFORM;
  }

  argsPlaceholder = `{"key":"value"}`;
  responsePlaceholder = `{"result":"value"}`;

  ngOnInit() {
    const testCase: TestCase = this?.data?.testCase;
    const testType: TestType = this?.data?.testType ?? TestType.TEST_TYPE_CHAT;
    this.form = new TestCaseForm(testCase, testType);

    // The test type is fixed for the lifetime of the modal, so the medium is pinned once.
    this.form.constraints.setForceMedium(
      this.form.isVoice ? InvocationContextMedium.VOICE : InvocationContextMedium.TEXT,
    );
  }

  protected syncHintDismissed = false;

  get showSyncHint(): boolean {
    if (this.syncHintDismissed || !this.data?.testCase?.id || !this.form?.scenarioActive || !this.form.scenarioPreexisting) {
      return false;
    }
    return this.form.scenario.dirty !== this.form.inputEdited;
  }

  dismissSyncHint(): void {
    this.syncHintDismissed = true;
  }

  onIdCopied(): void {
    this.idCopied = true;
    setTimeout(() => (this.idCopied = false), 1500);
  }

  getChannelName(channel: InvocationContextChannel): string {
    return getChannelName(channel, this.translateService);
  }

  getAuthStateName(authState: InvocationContextAuthState): string {
    return getAuthStateName(authState, this.translateService);
  }

  getAppName(app: InvocationContextApp): string {
    return getAppName(app, this.translateService);
  }

  addChatHistory(): void {
    this.form.chatHistory.addMessage();
  }

  addExpectedToolCall(): void {
    this.form.expectedToolCalls.addToolCall();
  }

  addExpectedCitation(): void {
    this.form.expectedCitations.addCitation();
  }

  removeChatHistory(index: number): void {
    this.form.chatHistory.removeMessage(index);
  }

  removeExpectedToolCall(index: number): void {
    this.form.expectedToolCalls.removeToolCall(index);
  }

  removeExpectedCitation(index: number): void {
    this.form.expectedCitations.removeCitation(index);
  }

  close(): void {
    this.dialogRef.close();
  }

  save(): void {
    if (!this.form.valid) {
      return;
    }
    this.dialogRef.close(this.form.toTestCase());
  }
}
